import { z } from 'zod';
import { lenientString, lenientUuid, lenientEnum, lenientRequiredNode } from '../schema-extensions';
import { IncomeType } from '../enums/income-type.enum';
import { Frequency } from '../enums/frequency.enum';
import { FrequencyValueNode } from './frequency-value-node-schema';

/**
 * Validation constants for IncomeNode
 * Defined before schemas to avoid forward reference issues
 */
const INCOME_CONSTANTS = {
  SOURCE_MAX_LENGTH: 100,
  OWNER_ID_LENGTH: 36,
} as const;

/**
 * Lenient schema for deserializing IncomeNode from storage
 * Handles missing fields and data migrations gracefully
 */
const IncomeNodeDeserializeSchema = z.object({
  id: lenientUuid(),
  type: lenientEnum(IncomeType, IncomeType.OTHER),
  source: lenientString(INCOME_CONSTANTS.SOURCE_MAX_LENGTH, ''),
  ownerId: lenientString(INCOME_CONSTANTS.OWNER_ID_LENGTH, '').optional(),
  value: lenientRequiredNode(FrequencyValueNode)
}).transform(val => ({
  id: val.id,
  type: val.type,
  source: val.source,
  ownerId: val.ownerId,
  value: val.value
}));

/**
 * Strict schema for creating new IncomeNode
 * Enforces validation rules for new data
 */
const IncomeNodeCreateSchema = z.object({
  id: z.string().uuid(),
  type: z.nativeEnum(IncomeType),
  source: z.string().min(1, 'Source is required').max(INCOME_CONSTANTS.SOURCE_MAX_LENGTH),
  ownerId: z.string().uuid().optional(),
  value: z.unknown().transform(val => FrequencyValueNode.create(val as Partial<FrequencyValueNode>))
}).transform(val => ({
  id: val.id,
  type: val.type,
  source: val.source,
  ownerId: val.ownerId,
  value: val.value
}));

/**
 * IncomeNode interface
 * Represents an income source in the vault
 */
export interface IncomeNode {
  id: string;
  type: IncomeType;
  source: string;
  ownerId?: string;
  value: FrequencyValueNode;
}

/**
 * IncomeNode namespace with factory and deserialization methods
 */
export namespace IncomeNode {
  /**
   * Validation constants for IncomeNode
   */
  export const Constants = INCOME_CONSTANTS;

  /**
   * Deserialize IncomeNode from unknown data (e.g., from storage)
   * Uses lenient validation with automatic defaults for missing fields
   * 
   * @param data Unknown data to deserialize
   * @returns Valid IncomeNode with defaults applied
   */
  export function deserialize(data: unknown): IncomeNode {
    return IncomeNodeDeserializeSchema.parse(data);
  }

  /**
   * Safely deserialize IncomeNode with error handling
   * 
   * @param data Unknown data to deserialize
   * @returns Success result with data or failure result with error
   */
  export function deserializeSafe(data: unknown): 
    | { success: true; data: IncomeNode }
    | { success: false; error: z.ZodError } 
  {
    const result = IncomeNodeDeserializeSchema.safeParse(data);
    return result.success
      ? { success: true, data: result.data }
      : { success: false, error: result.error };
  }

  /**
   * Create a new IncomeNode with strict validation
   * Use this for creating new incomes from user input
   * 
   * @param data Partial income data
   * @returns Valid IncomeNode
   * @throws ZodError if validation fails
   */
  export function create(data: Partial<IncomeNode>): IncomeNode {
    return IncomeNodeCreateSchema.parse({
      id: data.id ?? crypto.randomUUID(),
      ...data
    });
  }

  /**
   * Validate if data conforms to IncomeNode structure
   * 
   * @param data Data to validate
   * @returns True if data is a valid IncomeNode
   */
  export function validate(data: unknown): data is IncomeNode {
    return IncomeNodeDeserializeSchema.safeParse(data).success;
  }

  /**
   * Calculate annual income amount
   * 
   * @param node IncomeNode to calculate for
   * @returns Annual income amount
   */
  export function calculateAnnualAmount(node: IncomeNode): number {
    const { frequency, amount } = node.value;
    
    switch (frequency) {
      case Frequency.ANNUAL:
        return amount;
      case Frequency.MONTHLY:
        return amount * 12;
      case Frequency.WEEKLY:
        return amount * 52;
      case Frequency.DAILY:
        return amount * 365;
      case Frequency.ONE_TIME:
        return amount;
      default:
        return 0;
    }
  }

  /**
   * Check if income is currently active based on date range
   * 
   * @param node IncomeNode to check
   * @param referenceDate Date to check against (defaults to today)
   * @returns True if income is active on the reference date
   */
  export function isActive(node: IncomeNode, referenceDate: Date = new Date()): boolean {
    if (!node.value.from || !node.value.to) {
      return false;
    }

    const from = new Date(node.value.from);
    const to = new Date(node.value.to);
    
    return referenceDate >= from && referenceDate <= to;
  }
}
